/**
 * <citron-menu-drawer>: sections/citron-menu-drawer.liquid.
 *
 * Opens from any [data-ctr-menu-open] button on the page (the header's
 * hamburger), which stays disabled until this file has run so a tap during
 * loading is never swallowed. CSS owns the animation; this file owns state:
 * open/close, the iOS-safe scroll lock, the focus trap, push panels, the
 * finger drag, the banner slider and the desktop dropdown cards.
 */
(() => {
  if (customElements.get('citron-menu-drawer')) return;

  const still = matchMedia('(prefers-reduced-motion: reduce)');
  const FOCUS = 'a[href],button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])';

  class CitronMenuDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-dr-panel]');
      if (!this.panel) return;
      this.mid = this.querySelector('[data-dr-mid]');
      this.scroller = this.querySelector('[data-dr-scroll]');
      this.push = this.dataset.mode === 'push';
      this.ms = parseInt(getComputedStyle(this).getPropertyValue('--ctr-dr-ms'), 10) || 320;
      this.stack = [];
      this.shown = false;
      this.ac = new AbortController();
      const signal = this.ac.signal;
      const on = (el, type, fn, opts) => el && el.addEventListener(type, fn, { signal, ...opts });

      // Push panels move out of their row: nested sub-menus would otherwise be
      // clipped by, and scroll with, the panel above them.
      if (this.push) this.querySelectorAll('.ctr-nav__sub').forEach((sub) => this.mid.append(sub));

      this.querySelectorAll('.ctr-dr__stag').forEach((el, i) => el.style.setProperty('--i', i));

      document.querySelectorAll('[data-ctr-menu-open]').forEach((btn) => {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        on(btn, 'click', () => this.show(btn));
      });

      on(this, 'click', (event) => {
        const el = event.target;
        if (el.closest('[data-dr-close]')) return this.close();
        if (el.closest('[data-dr-back]')) return this.back();
        const opener = el.closest('[data-dr-open]');
        if (opener) this.toggleSub(opener);
      });

      on(this, 'keydown', (event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          if (this.stack.length) this.back();
          else this.close();
        } else if (event.key === 'Tab') {
          this.trap(event);
        }
      });

      on(this.scroller, 'scroll', () => this.classList.toggle('is-scrolled', this.scroller.scrollTop > 4), {
        passive: true,
      });

      // Theme editor: selecting the section or one of its blocks opens the
      // drawer so the merchant can see what they are editing.
      on(document, 'shopify:section:select', (e) => e.target.contains(this) && this.show());
      on(document, 'shopify:section:deselect', (e) => e.target.contains(this) && this.close());
      on(document, 'shopify:block:select', (e) => {
        if (!this.contains(e.target)) return;
        this.show();
        const row = e.target.querySelector('[data-dr-open]');
        if (row && !this.stack.length) this.toggleSub(row);
      });

      this.drag(on);
      this.banners(on);
      this.dress();
    }

    disconnectedCallback() {
      this.ac?.abort();
      clearInterval(this.timer);
      clearTimeout(this.hideTimer);
      if (this.shown) this.unlock();
    }

    /* ---------------------------------------------------------------- */
    /* Open and close                                                    */
    /* ---------------------------------------------------------------- */

    show(opener) {
      if (this.shown) return;
      this.shown = true;
      if (opener) this.opener = opener;
      clearTimeout(this.hideTimer);
      this.removeAttribute('inert');
      this.lock();
      this.panel.getBoundingClientRect(); // commit the closed transform first
      this.classList.add('is-open');
      this.opener?.setAttribute('aria-expanded', 'true');
      this.querySelector('.ctr-dr__x')?.focus({ preventScroll: true });
    }

    close() {
      if (!this.shown) return;
      this.shown = false;
      this.classList.remove('is-open');
      while (this.stack.length) this.back(true);
      this.opener?.setAttribute('aria-expanded', 'false');
      this.unlock();
      // Focus leaves before `inert` lands, or it would be lost to the body.
      this.opener?.focus({ preventScroll: true });
      this.hideTimer = setTimeout(() => this.setAttribute('inert', ''), still.matches ? 0 : this.ms);
    }

    lock() {
      this.y = window.scrollY;
      const bar = window.innerWidth - document.documentElement.clientWidth;
      const s = document.body.style;
      // position:fixed rather than overflow:hidden — iOS Safari ignores the
      // latter once the page has momentum.
      s.position = 'fixed';
      s.top = `${-this.y}px`;
      s.left = '0';
      s.right = '0';
      if (bar > 0) s.paddingRight = `${bar}px`; // no jump as the scrollbar goes
    }

    unlock() {
      const s = document.body.style;
      s.position = s.top = s.left = s.right = s.paddingRight = '';
      window.scrollTo(0, this.y || 0);
    }

    trap(event) {
      const items = [...this.querySelectorAll(FOCUS)].filter((el) => !el.closest('[inert]') && el.offsetParent);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const at = document.activeElement;
      if (event.shiftKey && (at === first || !this.contains(at))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && at === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ---------------------------------------------------------------- */
    /* Sub-menus                                                         */
    /* ---------------------------------------------------------------- */

    toggleSub(btn) {
      const sub = document.getElementById(btn.getAttribute('aria-controls'));
      if (!sub) return;
      if (sub.classList.contains('is-open')) return this.back();

      btn.setAttribute('aria-expanded', 'true');
      sub.classList.add('is-open');
      sub.removeAttribute('inert');
      this.stack.push({ sub, btn });
      if (!this.push) return;
      // A push panel covers the list, so the list must leave the tab order too.
      this.scroller?.setAttribute('inert', '');
      sub.querySelector('[data-dr-back]')?.focus({ preventScroll: true });
    }

    back(silent) {
      const top = this.stack.pop();
      if (!top) return;
      top.sub.classList.remove('is-open');
      top.sub.setAttribute('inert', '');
      top.btn.setAttribute('aria-expanded', 'false');
      if (!this.stack.length) this.scroller?.removeAttribute('inert');
      if (!silent) top.btn.focus({ preventScroll: true });
    }

    /* ---------------------------------------------------------------- */
    /* Finger drag: the panel follows, then snaps                        */
    /* ---------------------------------------------------------------- */

    drag(on) {
      if (still.matches || !window.PointerEvent) return;
      // Closing direction: -1 for a left drawer, +1 for a right one.
      const dir = this.dataset.side === 'right' ? 1 : -1;
      let sx = 0;
      let sy = 0;
      let t0 = 0;
      let dx = 0;
      let live = false;
      let axis = null;
      let sub = null;

      on(this.panel, 'pointerdown', (e) => {
        if (e.pointerType === 'mouse' || e.target.closest('[data-dr-bantrack]')) return;
        sx = e.clientX;
        sy = e.clientY;
        t0 = e.timeStamp;
        dx = 0;
        axis = null;
        live = true;
        sub = this.push && this.stack.length ? this.stack[this.stack.length - 1].sub : null;
      });

      on(window, 'pointermove', (e) => {
        if (!live) return;
        const ax = e.clientX - sx;
        const ay = e.clientY - sy;
        if (axis === null) {
          if (Math.abs(ax) < 8 && Math.abs(ay) < 8) return;
          // Vertical wins ties, so scrolling the list never closes the menu.
          axis = Math.abs(ax) > Math.abs(ay) * 1.3;
          if (!axis) return (live = false);
          this.classList.add('is-dragging');
        }
        // A sub-panel only goes back (rightwards); the panel only closes.
        dx = sub ? Math.max(0, ax) : dir < 0 ? Math.min(0, ax) : Math.max(0, ax);
        if (sub) sub.style.setProperty('--ctr-dr-sx', `${dx}px`);
        else this.style.setProperty('--ctr-dr-x', `${dx}px`);
      });

      const end = (e) => {
        if (!live) return;
        live = false;
        if (!axis) return;
        this.classList.remove('is-dragging');
        const width = (sub || this.panel).getBoundingClientRect().width || 1;
        const speed = Math.abs(dx) / Math.max(1, e.timeStamp - t0);
        const past = Math.abs(dx) / width > 0.35 || speed > 0.5;
        if (sub) {
          sub.style.removeProperty('--ctr-dr-sx');
          if (past) this.back();
        } else {
          this.style.removeProperty('--ctr-dr-x');
          if (past) this.close();
        }
      };

      on(window, 'pointerup', end);
      on(window, 'pointercancel', end);
    }

    /* ---------------------------------------------------------------- */
    /* Banner slider (scroll maths shared with citron-carousel.js)       */
    /* ---------------------------------------------------------------- */

    banners(on) {
      const track = this.querySelector('[data-dr-bantrack]');
      const dots = [...this.querySelectorAll('[data-dr-dot]')];
      if (!track || dots.length < 2 || !window.CitronCarousel) return;
      const T = window.CitronCarousel.track;
      const slides = [...track.children];

      on(
        track,
        'scroll',
        () => {
          cancelAnimationFrame(this.frame);
          this.frame = requestAnimationFrame(() => {
            this.at = T.nearest(track, slides);
            dots.forEach((dot, k) => dot.setAttribute('aria-current', k === this.at));
          });
        },
        { passive: true }
      );
      // Any touch ends autoplay for the session: it is a menu, not a carousel.
      on(track, 'pointerdown', () => (this.auto = false));
      dots.forEach((dot, i) =>
        on(dot, 'click', () => {
          this.auto = false;
          T.go(track, slides[i]);
        })
      );

      const secs = Number(this.dataset.autoplay);
      if (!secs || still.matches) return;
      this.auto = true;
      this.timer = setInterval(() => {
        if (!this.shown || !this.auto) return;
        T.go(track, slides[((this.at || 0) + 1) % slides.length]);
      }, secs * 1000);
    }

    /* ---------------------------------------------------------------- */
    /* Desktop: the header's dropdowns borrow these child cards, so the  */
    /* images are configured once, here.                                 */
    /* ---------------------------------------------------------------- */

    dress() {
      if (!matchMedia('(min-width: 990px)').matches) return;
      document.querySelectorAll('[data-ctr-dd-key]').forEach((item) => {
        const grid = item.querySelector('.ctr-hdr__dd-grid');
        const cards = this.querySelector(
          `.ctr-nav__sub[data-key="${CSS.escape(item.dataset.ctrDdKey)}"] .ctr-nav__cards`
        );
        if (grid && cards) grid.replaceWith(cards.cloneNode(true));
      });
    }
  }

  customElements.define('citron-menu-drawer', CitronMenuDrawer);
})();
